/**
 * A standalone appointment: the visit, the patient, who sees and recorded them, how far the visit
 * has come (booked, recorded, transcribed, note, signed), the approved diagnoses, and what this
 * user may do next. Connected organizations keep the old behavior: /appointments/:id sends them to
 * the recorder or the review.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ArrowLeft, CheckCircle2, Circle, Loader2, AlertCircle, Mic, FileText, Pencil, Trash2, UserRound } from 'lucide-react';
import { appointmentService, type AppointmentOverview } from '../services/appointment';
import { useOrganizationStore } from '../stores/organizationStore';
import { ChangeProviderModal } from '../components/encounters/ChangeProviderModal';
import { EditAppointmentDialog } from '../components/appointments/EditAppointmentDialog';
import { VisitStatusDialog } from '../components/appointments/VisitStatusDialog';
import { ConfirmationDialog } from '../components/ui/ConfirmationDialog';
import AppointmentRedirect from '../components/AppointmentRedirect';
import { toast } from '../utils/toast';
import { ageOn, birthDay, visitSteps, type StepState } from '../utils/appointmentVisit';

const TYPE_LABELS: Record<string, string> = {
  consultation: 'Consultation', 'follow-up': 'Follow-up', procedure: 'Procedure', emergency: 'Emergency', other: 'Other',
};

const STEP_STYLE: Record<StepState, string> = {
  done: 'text-emerald-700', current: 'text-blue-700', failed: 'text-red-700', todo: 'text-gray-400',
};

// The icon and color carry each step's state visually; this text carries it to screen readers.
const STEP_STATE_TEXT: Record<StepState, string> = {
  done: 'done', current: 'in progress', failed: 'failed', todo: 'not started',
};

function StandaloneAppointmentDetails({ id }: { id: string }) {
  const navigate = useNavigate();
  const [overview, setOverview] = useState<AppointmentOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [changingProvider, setChangingProvider] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [statusChange, setStatusChange] = useState<'cancelled' | 'no-show' | 'scheduled' | null>(null);

  const load = useCallback(async () => {
    try {
      setOverview(await appointmentService.getOverview(id));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the appointment');
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="max-w-xl mx-auto py-16 px-4 text-center" role="alert">
        <AlertCircle className="w-10 h-10 text-red-600 mx-auto" />
        <h1 className="mt-4 text-xl font-semibold text-gray-900">Unable to load the appointment</h1>
        <p className="mt-2 text-gray-600">{error}</p>
        <Link to="/appointments" className="btn-primary inline-block mt-6">Back to Appointments</Link>
      </div>
    );
  }
  if (!overview) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-600 gap-3">
        <Loader2 className="w-6 h-6 animate-spin" /> Loading appointment...
      </div>
    );
  }

  const { appointment, patient, provider, actions } = overview;
  const when = new Date(appointment.scheduledDate);
  const dob = birthDay(patient?.dateOfBirth);
  const patientName = [patient?.firstName, patient?.lastName].filter(Boolean).join(' ') || 'Unnamed patient';
  const steps = visitSteps(overview);

  const remove = async () => {
    try {
      setDeleting(true);
      await appointmentService.deleteAppointment(appointment.id);
      toast.success('Appointment deleted');
      navigate('/appointments');
    } catch {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      <Link to="/appointments" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-4 h-4" /> Back to Appointments
      </Link>

      {/* Patient and visit */}
      <header className="bg-white border border-gray-200 rounded-xl p-5 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm text-gray-500">{TYPE_LABELS[appointment.type] ?? appointment.type} · {format(when, 'EEEE, MMMM d, yyyy')} at {format(when, 'h:mm a')}</p>
          {appointment.visitStatus !== 'scheduled' && (
            <p className="mt-2 inline-flex flex-wrap items-center gap-2 text-sm">
              <span className={`px-2 py-0.5 rounded-full font-medium ${appointment.visitStatus === 'cancelled' ? 'bg-gray-100 text-gray-700' : 'bg-orange-100 text-orange-800'}`}>
                {appointment.visitStatus === 'cancelled' ? 'Cancelled' : 'No-show'}
              </span>
              {appointment.visitStatusReason && <span className="text-gray-600">{appointment.visitStatusReason}</span>}
            </p>
          )}
          <h1 className="mt-1 text-2xl font-semibold text-gray-900">
            {patient ? <Link to={`/patients/${patient.id}`} className="hover:underline">{patientName}</Link> : patientName}
          </h1>
          <p className="mt-1 text-sm text-gray-600 flex flex-wrap gap-x-3">
            {patient?.mrn ? <span>MRN: {patient.mrn}</span> : <span>No MRN</span>}
            {dob && <span>DOB: {format(dob, 'MM/dd/yyyy')} ({ageOn(dob)} y)</span>}
            {patient?.gender && <span className="capitalize">{patient.gender}</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {actions.canRecord && (
            <button type="button" className="btn-primary inline-flex items-center gap-2" onClick={() => navigate(`/recording/${appointment.id}`)}>
              <Mic className="w-4 h-4" /> Record visit
            </button>
          )}
          {actions.canOpenReview && (
            <button type="button" className="btn-primary inline-flex items-center gap-2" onClick={() => navigate(`/case/${appointment.id}/validation`)}>
              <FileText className="w-4 h-4" /> {overview.note.state === 'signed' ? 'View note' : 'Review note'}
            </button>
          )}
          {actions.canEditDetails && (
            <button type="button" className="btn-secondary inline-flex items-center gap-2" onClick={() => setEditing(true)}>
              <Pencil className="w-4 h-4" /> Edit
            </button>
          )}
          {actions.canMarkNoShow && (
            <button type="button" className="btn-secondary" onClick={() => setStatusChange('no-show')}>Mark no-show</button>
          )}
          {actions.canCancel && (
            <button type="button" className="btn-secondary" onClick={() => setStatusChange('cancelled')}>Cancel visit</button>
          )}
          {actions.canReinstate && (
            <button type="button" className="btn-secondary" onClick={() => setStatusChange('scheduled')}>Put back on schedule</button>
          )}
          {actions.canDelete && (
            <button type="button" className="btn-secondary inline-flex items-center gap-2 text-red-700" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
        </div>
      </header>

      {/* Progress */}
      <section aria-label="Visit progress" className="bg-white border border-gray-200 rounded-xl p-5">
        <ol className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          {steps.map(step => (
            <li key={step.label} className={`flex items-start gap-2 ${STEP_STYLE[step.state]}`}>
              {step.state === 'done' ? <CheckCircle2 className="w-5 h-5 shrink-0" />
                : step.state === 'failed' ? <AlertCircle className="w-5 h-5 shrink-0" />
                : step.state === 'current' ? <Loader2 className="w-5 h-5 shrink-0" />
                : <Circle className="w-5 h-5 shrink-0" />}
              <div>
                <div className="text-sm font-medium">
                  {step.label}
                  {step.state === 'failed' && ['Recorded', 'Transcribed'].includes(step.label) && <span aria-hidden="true"> failed</span>}
                  <span className="sr-only">, {STEP_STATE_TEXT[step.state]}</span>
                </div>
                {step.detail && <div className="text-xs text-gray-500">{step.detail}</div>}
              </div>
            </li>
          ))}
        </ol>
      </section>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Visit */}
        <section className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
          <h2 className="text-lg font-semibold text-gray-900">Visit</h2>
          <dl className="grid grid-cols-[8rem_1fr] gap-y-2 text-sm">
            <dt className="text-gray-500">Date</dt><dd>{format(when, 'MMM d, yyyy · h:mm a')}</dd>
            <dt className="text-gray-500">Type</dt><dd>{TYPE_LABELS[appointment.type] ?? appointment.type}</dd>
            <dt className="text-gray-500">Location</dt><dd>{appointment.location || '—'}</dd>
            <dt className="text-gray-500">Reason</dt><dd>{appointment.chiefComplaint || 'Not recorded'}</dd>
          </dl>
        </section>

        {/* People */}
        <section className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
          <h2 className="text-lg font-semibold text-gray-900">People</h2>
          <dl className="grid grid-cols-[8rem_1fr] gap-y-2 text-sm items-center">
            <dt className="text-gray-500">Provider</dt>
            <dd className="flex items-center gap-2">
              <UserRound className="w-4 h-4 text-gray-400" /> {provider?.name ?? 'Unassigned'}
              {actions.canChangeProvider && (
                <button type="button" className="text-xs font-medium text-blue-700 hover:underline" onClick={() => setChangingProvider(true)}>Change</button>
              )}
            </dd>
            <dt className="text-gray-500">Recorded by</dt><dd>{overview.recordedBy?.name ?? 'Not recorded yet'}</dd>
          </dl>
        </section>
      </div>

      {/* Coding */}
      <section className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
        <h2 className="text-lg font-semibold text-gray-900">Diagnoses (ICD-10)</h2>
        {overview.diagnoses.length ? (
          <ul className="divide-y divide-gray-100">
            {overview.diagnoses.map(d => (
              <li key={d.code} className="py-2 flex gap-3 text-sm">
                <span className="font-mono text-gray-700 w-20 shrink-0">{d.code}</span><span>{d.description}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500">No diagnoses approved yet.</p>
        )}
      </section>

      {statusChange && (
        <VisitStatusDialog appointmentId={appointment.id} change={statusChange} onClose={() => setStatusChange(null)} onChanged={load} />
      )}
      {editing && <EditAppointmentDialog appointment={appointment} onClose={() => setEditing(false)} onSaved={load} />}
      {changingProvider && (
        <ChangeProviderModal
          item={{ id: appointment.id, patientName, providerName: provider?.name } as never}
          onClose={() => setChangingProvider(false)}
          onChanged={load}
        />
      )}
      <ConfirmationDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete this appointment?"
        message={`${patientName}, ${format(when, 'MMM d, yyyy · h:mm a')}. Nothing was recorded for it, so nothing clinical is lost.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleting}
        onConfirm={remove}
      />
    </div>
  );
}

/** /appointments/:id */
export default function AppointmentDetails() {
  const { id } = useParams<{ id: string }>();
  const { operationMode } = useOrganizationStore();
  if (!id) return null;
  return operationMode === 'standalone' ? <StandaloneAppointmentDetails id={id} /> : <AppointmentRedirect />;
}
